// Admin Territory Builder.
//
// Flow:
//   1. Search a postcode → marker dropped, surrounding sectors loaded
//   2. Adjust radius (5-30 km) → reload sectors
//   3. Click sector dots on the map (or chips below) to add/remove from
//      the territory. Live home counter at the top targets 150.
//   4. Save → either as a brand-new "Territory Plan" linked to a contact
//      or update an existing plan. Plans are editable and persisted.
import { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import TerritoryMap from "@/components/territory/TerritoryMap";
import TerritoryProposalSummary from "@/components/territory/TerritoryProposalSummary";
import { proposalChanges, proposalTotals, editProposalSector } from "@/components/territory/territoryProposal";
import {
  Search, Loader2, Target, Save, Trash2, MapPin, Plus, RotateCcw,
  Users, AlertCircle, CheckCircle2, Pencil, ChevronRight, ArrowLeft,
  ClipboardPaste, Layers, Eye, EyeOff, ChevronDown, ChevronUp,
  Share2, Copy, Link as LinkIcon, FolderOpen, History, X,
  Lock, Unlock,
} from "lucide-react";
import { buildProspectMailto, NO_EMAIL_MESSAGE } from "@/pages/territoryBuilder/prospectMailto";

const TARGET_HOMES = 150;
const KM_PER_MI = 1.609344;

export default function TerritoryBuilderPage() {
  const [params] = useSearchParams();
  const contactId = params.get("contact_id") || null;
  const planId = params.get("plan_id") || null;
  const franchiseeId = params.get("franchisee_id") || null;
  const [proposalFranchiseeId, setProposalFranchiseeId] = useState(params.get("proposal_for") || null);
  const isProposal = !franchiseeId && !!proposalFranchiseeId;
  const [baselineSectors, setBaselineSectors] = useState([]);
  const [proposalMode, setProposalMode] = useState("remove");
  const [proposalReady, setProposalReady] = useState(false);


  const [postcode, setPostcode] = useState("");
  const [centre, setCentre] = useState(null);
  const [centreLabel, setCentreLabel] = useState("");
  const [radiusMi, setRadiusMi] = useState(10); // miles
  const [sectors, setSectors] = useState([]);
  const [selected, setSelected] = useState([]);
  const changes = useMemo(() => proposalChanges(baselineSectors, selected), [baselineSectors, selected]);
  const geometryCodes = useMemo(() => isProposal ? [...new Set([...baselineSectors, ...selected])] : selected, [isProposal, baselineSectors, selected]);
  const [loading, setLoading] = useState(false);
  const [savedPlan, setSavedPlan] = useState(null);
  const [applyingProposal, setApplyingProposal] = useState(false);
  const proposalApplied = savedPlan?.apply_status === "applied";
  const [err, setErr] = useState("");
  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");
  const [contact, setContact] = useState(null);
  const [existingPlans, setExistingPlans] = useState([]);
  const [saving, setSaving] = useState(false);
  const [franchisee, setFranchisee] = useState(null);
  const [territorySavedAt, setTerritorySavedAt] = useState(null);
  // ── Save toast + history (rollback safety) ─────────────────────────
  // `saveToast` shows an explicit success/failure banner immediately
  // after a save click — the OLD UI just updated a tiny timestamp pill
  // which was too easy to miss, leading to silent "did it save?"
  // confusion. `history` holds the last 50 snapshots from
  // `territory_history` so the admin can compare AND restore.
  const [saveToast, setSaveToast] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [rollingBack, setRollingBack] = useState(null);
  // Auto-dismiss successful save toast after 6s; keep failure toast
  // sticky until the admin acknowledges it.
  useEffect(() => {
    if (!saveToast?.ok) return;
    const t = setTimeout(() => setSaveToast(null), 6000);
    return () => clearTimeout(t);
  }, [saveToast]);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [pastePreview, setPastePreview] = useState(null);
  // Banner for the "Copy all live franchisee sectors" action — shows a
  // success or error message above the toolbar for ~3s.
  const [copyAllStatus, setCopyAllStatus] = useState("");
  const [copyingAll, setCopyingAll] = useState(false);
  // Overlay: every active franchisee's locked territory. Loaded once on mount,
  // refreshed whenever the lock-target changes so the franchisee being edited
  // isn't duplicated in the background.
  const [overlay, setOverlay] = useState({ franchisees: [], geojson: null, outlines: null });
  const [showOverlay, setShowOverlay] = useState(true);
  // Legend panel is collapsible — admins who already know the colours can
  // tuck it away to give the map more vertical real estate. Persisted across
  // sessions so the preference sticks.
  const [legendOpen, setLegendOpen] = useState(() => {
    try { return localStorage.getItem("cm.tb.legendOpen") !== "0"; }
    catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem("cm.tb.legendOpen", legendOpen ? "1" : "0"); }
    catch (e) { console.debug("[TerritoryBuilder] localStorage write blocked", e); }
  }, [legendOpen]);

  // Selected / Nearby sectors panels now collapsible below the map — defaults
  // closed so the map gets all the screen real-estate when admins land here.
  const [selectedListOpen, setSelectedListOpen] = useState(() => {
    try { return localStorage.getItem("cm.tb.selectedOpen") === "1"; }
    catch { return false; }
  });
  const [nearbyListOpen, setNearbyListOpen] = useState(() => {
    try { return localStorage.getItem("cm.tb.nearbyOpen") === "1"; }
    catch { return false; }
  });
  useEffect(() => { try { localStorage.setItem("cm.tb.selectedOpen", selectedListOpen ? "1" : "0"); } catch (e) { console.debug("[TerritoryBuilder] localStorage write blocked", e); } }, [selectedListOpen]);
  const [mapTall, setMapTall] = useState(() => {
    try { return localStorage.getItem("cm.tb.mapTall") === "1"; }
    catch { return false; }
  });
  useEffect(() => { try { localStorage.setItem("cm.tb.mapTall", mapTall ? "1" : "0"); } catch (e) { console.debug("[TerritoryBuilder] localStorage write blocked", e); } }, [mapTall]);
  useEffect(() => { try { localStorage.setItem("cm.tb.nearbyOpen", nearbyListOpen ? "1" : "0"); } catch (e) { console.debug("[TerritoryBuilder] localStorage write blocked", e); } }, [nearbyListOpen]);

  // All saved plans — listed in the bottom-right "Saved plans" panel when
  // there's no contact/franchisee context (e.g. opened via the sidebar).
  // Lets admins re-open prior prospect plans + share/copy/delete them.
  const [allPlans, setAllPlans] = useState([]);
  const [allPlansLoading, setAllPlansLoading] = useState(false);
  const [planFilter, setPlanFilter] = useState("");

  // Saved plans overlay — admin can tick "Show on map" against any saved
  // plan to paint its sectors in amber over the main map. Purpose: gives
  // admins a visual reference of nearby "potential areas" while drawing a
  // new territory so they don't accidentally overlap prior scoping work.
  // ``overlayPlanIds`` is the set of currently-visible plan IDs;
  // ``overlayPlansData`` is the fetched GeoJSON + centroid labels keyed
  // by plan id.
  const [overlayPlanIds, setOverlayPlanIds] = useState(() => new Set());
  const [overlayPlansData, setOverlayPlansData] = useState({ geojson: { type: "FeatureCollection", features: [] }, labels: [] });
  const toggleOverlayPlan = useCallback((planId) => {
    setOverlayPlanIds((cur) => {
      const next = new Set(cur);
      if (next.has(planId)) next.delete(planId); else next.add(planId);
      return next;
    });
  }, []);
  // Fetch polygons for the union of sectors across all overlayed plans,
  // then build per-plan features + a centroid for each plan's label pill.
  useEffect(() => {
    if (!overlayPlanIds.size) {
      setOverlayPlansData({ geojson: { type: "FeatureCollection", features: [] }, labels: [] });
      return;
    }
    const plansToShow = allPlans.filter((p) => overlayPlanIds.has(p.id));
    if (!plansToShow.length) return;
    const allSectors = Array.from(new Set(plansToShow.flatMap((p) => p.sectors || []))).filter(Boolean);
    if (!allSectors.length) {
      setOverlayPlansData({ geojson: { type: "FeatureCollection", features: [] }, labels: [] });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/territory/sector-polygons", { params: { sectors: allSectors.join(",") } });
        if (cancelled) return;
        const geomBySector = new Map((data.sectors || []).map((s) => [s.sector, s.geometry]));
        const features = [];
        const labels = [];
        for (const plan of plansToShow) {
          let sumLng = 0, sumLat = 0, nPts = 0;
          for (const code of plan.sectors || []) {
            const geom = geomBySector.get(code);
            if (!geom) continue;
            features.push({
              type: "Feature",
              properties: { plan_id: plan.id, plan_name: plan.name || "Untitled" },
              geometry: geom,
            });
            // Walk coords to accumulate centroid — cheap arithmetic mean is fine visually.
            const walk = (coords) => {
              if (typeof coords[0] === "number") {
                sumLng += coords[0]; sumLat += coords[1]; nPts += 1;
              } else {
                coords.forEach(walk);
              }
            };
            walk(geom.coordinates);
          }
          if (nPts > 0) {
            labels.push({ id: plan.id, name: plan.name || "Untitled", lng: sumLng / nPts, lat: sumLat / nPts });
          }
        }
        setOverlayPlansData({ geojson: { type: "FeatureCollection", features }, labels });
      } catch (e) {
        console.warn("[TerritoryBuilder] Saved-plans overlay polygon fetch failed", e);
      }
    })();
    return () => { cancelled = true; };
  }, [overlayPlanIds, allPlans]);

  const reloadAllPlans = useCallback(async () => {
    setAllPlansLoading(true);
    try {
      const { data } = await api.get("/territory-plans");
      setAllPlans(data.plans || []);
    } catch (e) {
      console.error("[TerritoryBuilder] Failed to load saved plans", e);
    }
    finally { setAllPlansLoading(false); }
  }, []);

  useEffect(() => {
    if (contactId || franchiseeId) return;  // panel only relevant globally
    reloadAllPlans();
  }, [contactId, franchiseeId, reloadAllPlans]);

  // Load contact details + existing plans if contact_id is provided
  useEffect(() => {
    if (!contactId) return;
    (async () => {
      let loaded = null;
      try {
        const c = await api.get(`/contacts/${contactId}`);
        // Endpoint returns {contact: {...}, _source_collection: "..."} so
        // unwrap the nested object (falling back to the raw data for any
        // older code-paths that already returned a flat contact).
        loaded = c.data?.contact || c.data;
        setContact(loaded);
      } catch (e) {
        console.error("[TerritoryBuilder] Failed to load contact", contactId, e);
      }
      try {
        const p = await api.get("/territory-plans", { params: { contact_id: contactId } });
        setExistingPlans(p.data.plans || []);
      } catch (e) {
        console.error("[TerritoryBuilder] Failed to load existing plans for contact", contactId, e);
      }
      // Auto-prefill the postcode search from the contact's own postcode
      // so the map centres on them straight away. We only do this when:
      //   * a specific plan hasn't been requested (plan_id wins),
      //   * the postcode input is still empty (user hasn't typed yet),
      //   * the contact actually has a postcode on file.
      // The postcode-lookup call is fire-and-forget — a failure just
      // leaves the field pre-populated so the admin can hit Search.
      const contactPostcode = (loaded?.postcode || "").trim();
      if (!planId && contactPostcode && !postcode) {
        setPostcode(contactPostcode);
        try {
          const r = await api.get("/territory/postcode-lookup", {
            params: { postcode: contactPostcode },
          });
          if (r.data?.latitude != null) {
            setCentre({ lat: r.data.latitude, lng: r.data.longitude });
            const placeBit = r.data.matched_place ? `${r.data.matched_place} · ` : "";
            setCentreLabel(
              `${placeBit}${r.data.postcode} · ${r.data.admin_district || r.data.region || ""}`,
            );
            setPostcode(r.data.postcode);
          }
        } catch (e) {
          // Silent — the postcode may be malformed on the contact, in
          // which case we simply leave the raw value in the input.
          console.warn("[TerritoryBuilder] contact postcode prefill lookup failed", e);
        }
      }
    })();
  }, [contactId, planId]);

  useEffect(() => {
    if (!proposalFranchiseeId || planId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/franchisees/${proposalFranchiseeId}/territory`);
        if (cancelled) return;
        if (!data.territory_sectors?.length) throw new Error("This franchisee has no agreed territory to use as a baseline.");
        setFranchisee(data);
        setBaselineSectors(data.territory_sectors);
        setSelected(data.territory_sectors);
        setName(`Territory changes — ${data.organisation || "#" + data.franchise_number}`);
        setProposalReady(true);
        if (data.postcode) {
          setPostcode(data.postcode);
          try {
            const r = await api.get("/territory/postcode-lookup", { params: { postcode: data.postcode } });
            if (!cancelled && r.data.latitude != null) {
              setCentre({ lat: r.data.latitude, lng: r.data.longitude });
              setCentreLabel(`${data.organisation || ""} · ${r.data.postcode}`);
            }
          } catch { /* Sector geometry still displays without an HQ lookup. */ }
        }
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || e.message || "Could not load agreed territory.");
      }
    })();
    return () => { cancelled = true; };
  }, [proposalFranchiseeId, planId]);

  // Hydrate existing franchisee territory
  useEffect(() => {
    if (!franchiseeId) return;
    (async () => {
      try {
        const { data } = await api.get(`/franchisees/${franchiseeId}/territory`);
        setFranchisee(data);
        setSelected(data.territory_sectors || []);
        setTerritorySavedAt(data.territory_updated_at);
        if (data.postcode) {
          setPostcode(data.postcode);
          // Auto-lookup so map centres on HQ
          try {
            const r = await api.get("/territory/postcode-lookup", { params: { postcode: data.postcode } });
            if (r.data.latitude != null) {
              setCentre({ lat: r.data.latitude, lng: r.data.longitude });
              setCentreLabel(`${data.organisation || ""} · ${r.data.postcode}`);
            }
          } catch (e) {
            console.error("[TerritoryBuilder] Postcode lookup failed", e);
          }
        }
        reloadHistory();
      } catch (e) {
        console.error("[TerritoryBuilder] Failed to load franchisee territory", franchiseeId, e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [franchiseeId]);

  const reloadHistory = async () => {
    if (!franchiseeId) return;
    try {
      const { data } = await api.get(`/franchisees/${franchiseeId}/territory/history`);
      setHistory(data.items || []);
    } catch (e) {
      // Audit log is best-effort UI — never block on it.
      console.debug("[TerritoryBuilder] history fetch failed", e);
    }
  };

  const rollbackTo = async (snap) => {
    if (!franchiseeId || !snap?.id) return;
    const confirmMsg = `Roll back to ${new Date(snap.changed_at).toLocaleString()}?\n\nThis restores ${snap.previous_sectors?.length || 0} sector(s) (was ${snap.new_sectors?.length || 0}).\nThe rollback is itself recorded — you can undo it.`;
    if (!window.confirm(confirmMsg)) return;
    setRollingBack(snap.id);
    try {
      const { data } = await api.post(`/franchisees/${franchiseeId}/territory/rollback/${snap.id}`);
      // Sync local UI to the restored state.
      setSelected(data.sectors);
      setTerritorySavedAt(new Date().toISOString());
      setFranchisee((cur) => cur ? { ...cur, territory_sectors: data.sectors, territory_home_count: data.home_count } : cur);
      setSaveToast({
        ok: true,
        message: `Rolled back to ${data.sectors.length} sector${data.sectors.length === 1 ? "" : "s"} · ${data.home_count} home${data.home_count === 1 ? "" : "s"}`,
      });
      reloadHistory();
    } catch (e) {
      setSaveToast({ ok: false, message: `Rollback failed · ${e?.response?.data?.detail || e.message}` });
    } finally {
      setRollingBack(null);
    }
  };

  // Hydrate an existing plan
  useEffect(() => {
    if (!planId) return;
    (async () => {
      try {
        const { data } = await api.get("/territory-plans", { params: {} });
        const found = (data.plans || []).find((p) => p.id === planId);
        if (!found) return;
        if (found.plan_type === "territory_change") {
          setProposalFranchiseeId(found.franchisee_id);
          setBaselineSectors(found.baseline_sectors || []);
          setFranchisee({ id: found.franchisee_id, organisation: found.franchisee_name });
          setProposalReady(true);
        } else {
          setProposalFranchiseeId(null); setBaselineSectors([]); setProposalReady(false);
        }
        setSavedPlan(found);
        setName(found.name || "");
        setNotes(found.notes || "");
        setSelected(found.sectors || []);
        setSuggestedRemovals(found.suggested_removals || []);
        setShowRemovalOverlay(!!found.show_removal_overlay);
        setShowCounties(!!found.show_counties);
        if (found.centre_lat && found.centre_lng) {
          setCentre({ lat: found.centre_lat, lng: found.centre_lng });
          setCentreLabel(found.centre_postcode || "");
          setPostcode(found.centre_postcode || "");
        }
      } catch (e) {
        console.error("[TerritoryBuilder] Failed to hydrate plan", planId, e);
      }
    })();
  }, [planId]);

  // Load every active franchisee's locked territory so admins can draw
  // prospect plans against existing boundaries. Re-fetches when the lock
  // target changes so we never duplicate the franchisee being edited in
  // both the active layer and the background overlay.
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/territory/all-franchisees", {
          params: (franchiseeId || proposalFranchiseeId) ? { exclude_id: franchiseeId || proposalFranchiseeId } : {},
        });
        const data = res.data;
        setOverlay({
          franchisees: data.franchisees || [],
          geojson: data.geojson || null,
          outlines: data.outlines || null,
        });
        // Phase 3 diagnostic — cache computed_at comes via the
        // pre-serialised fast path header OR the slow-path _cache
        // block, depending on whether we're excluding a franchisee.
        const computedAt = data._cache?.computed_at
          || res.headers?.["x-atlas-computed-at"]
          || res.headers?.get?.("x-atlas-computed-at");
        if (computedAt) setAtlasComputedAt(computedAt);
      } catch (e) {
        console.warn("[TerritoryBuilder] Overlay (other franchisees) failed to load — non-critical", e);
      }
    })();
  }, [franchiseeId, proposalFranchiseeId]);

  const [atlasComputedAt, setAtlasComputedAt] = useState(null);
  const [atlasRefreshing, setAtlasRefreshing] = useState(false);
  const refreshAtlas = useCallback(async () => {
    setAtlasRefreshing(true);
    try {
      const { data } = await api.post("/territory/atlas/refresh");
      const { data: fresh } = await api.get("/territory/all-franchisees", {
        params: (franchiseeId || proposalFranchiseeId) ? { exclude_id: franchiseeId || proposalFranchiseeId } : {},
      });
      setOverlay({
        franchisees: fresh.franchisees || [],
        geojson: fresh.geojson || null,
        outlines: fresh.outlines || null,
      });
      setAtlasComputedAt(data?.meta?.computed_at || new Date().toISOString());
    } catch (e) {
      console.warn("[TerritoryBuilder] atlas refresh failed", e);
    } finally {
      setAtlasRefreshing(false);
    }
  }, [franchiseeId, proposalFranchiseeId]);

  // "Refreshed N min ago" ticks once a minute so the label stays live
  // without eating render cycles.
  const [nowTick, setNowTick] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);
  const atlasAgeLabel = useMemo(() => {
    if (!atlasComputedAt) return null;
    const ageMs = nowTick - new Date(atlasComputedAt).getTime();
    if (ageMs < 60_000) return "just now";
    const mins = Math.floor(ageMs / 60_000);
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.floor(mins / 60);
    return `${hrs} hr ago`;
  }, [atlasComputedAt, nowTick]);

  // Refresh sectors-near when centre or radius changes. When in
  // franchisee-lock mode, also fetch the geometry of every owned sector
  // (even those outside the search radius) so the map always shows
  // the full territory.
  const refreshSectors = useCallback(async () => {
    if (!centre && !franchiseeId) return;
    setLoading(true); setErr("");
    try {
      let near = { sectors: [] };
      if (centre) {
        const { data } = await api.get("/territory/sectors-near", {
          params: { lat: centre.lat, lon: centre.lng, radius_km: radiusMi * KM_PER_MI },
        });
        near = data;
      }
      let owned = [];
      const ownedCodes = (franchiseeId && franchisee?.territory_sectors) || [];
      if (ownedCodes.length) {
        const { data } = await api.get("/territory/sector-polygons", {
          params: { sectors: ownedCodes.join(",") },
        });
        owned = data.sectors || [];
      }
      // Merge — favouring "near" entries for distance info, but ensuring
      // every owned sector ends up in the feature list.
      const map = new Map();
      for (const s of near.sectors || []) map.set(s.sector, s);
      for (const s of owned) {
        if (!map.has(s.sector)) map.set(s.sector, { ...s, distance_km: 9999 });
      }
      setSectors(Array.from(map.values()));
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not load sectors.");
    } finally { setLoading(false); }
  }, [centre, radiusMi, franchiseeId, franchisee]);
  useEffect(() => { refreshSectors(); }, [refreshSectors]);

  // If ``selected`` contains sector codes that aren't yet in the map's
  // ``sectors`` feature list (typical when a saved plan is loaded and
  // its sectors sit outside the current search radius), top up the
  // geometry so the yellow "selected" overlay paints correctly. Runs
  // as a follow-up to refreshSectors rather than pulling ``selected``
  // into refreshSectors' deps — we don't want every sector toggle to
  // re-hit ``sectors-near``.
  useEffect(() => {
    if (!geometryCodes.length) return;
    const have = new Set(sectors.filter((s) => s.geometry).map((s) => s.sector));
    const missing = geometryCodes.filter((code) => code && !have.has(code));
    if (!missing.length) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/territory/sector-polygons", {
          params: { sectors: missing.join(",") },
        });
        if (cancelled) return;
        const extras = (data.sectors || []).map((s) => ({ ...s, distance_km: 9999 }));
        if (!extras.length) return;
        setSectors((cur) => {
          const m = new Map(cur.map((s) => [s.sector, s]));
          let added = 0;
          for (const s of extras) if (!m.has(s.sector)) { m.set(s.sector, s); added += 1; }
          // Bail out early if nothing new — otherwise the ``sectors``
          // reference changes on every render and this effect re-fires
          // in a tight loop.
          return added ? Array.from(m.values()) : cur;
        });
      } catch (e) {
        console.warn("[TerritoryBuilder] top-up sector polygons failed", e);
      }
    })();
    return () => { cancelled = true; };
  }, [geometryCodes, sectors]);

  const lookupPostcode = async () => {
    if (!postcode.trim()) return;
    setLoading(true); setErr("");
    try {
      const { data } = await api.get("/territory/postcode-lookup", { params: { postcode: postcode.trim() } });
      if (data.latitude == null) throw new Error("No coordinates");
      setCentre({ lat: data.latitude, lng: data.longitude });
      // When the lookup resolved a town/city name, surface that in the
      // label so the admin sees "Wokingham · RG40 1LX" rather than just
      // the looked-up postcode (which can feel unrelated to what they typed).
      const placeBit = data.matched_place ? `${data.matched_place} · ` : "";
      setCentreLabel(`${placeBit}${data.postcode} · ${data.admin_district || data.region || ""}`);
      setPostcode(data.postcode); // pin the resolved postcode in the input
    } catch (e) {
      setErr(e?.response?.data?.detail || "No matching UK postcode or place name");
    } finally { setLoading(false); }
  };

  const [editMode, setEditMode] = useState(() => !franchiseeId);
  const [dirty, setDirty] = useState(false);

  const toggleSector = useCallback((sec) => {
    if (isProposal) {
      if (!proposalReady || proposalApplied || applyingProposal) return;
      if (proposalMode === "add" && !baselineSectors.includes(sec) && overlay.franchisees.some((f) => (f.sectors || []).includes(sec))) {
        setErr(`${sec} belongs to another franchisee and cannot be added.`);
        return;
      }
      setErr("");
      setSelected((cur) => editProposalSector(cur, baselineSectors, sec, proposalMode));
      setDirty(true);
      return;
    }
    if (franchiseeId && !editMode) return;  // locked — silently ignore
    setSelected((cur) => cur.includes(sec) ? cur.filter((s) => s !== sec) : [...cur, sec]);
    if (franchiseeId) setDirty(true);
  }, [isProposal, proposalReady, baselineSectors, proposalMode, franchiseeId, editMode, overlay.franchisees, proposalApplied, applyingProposal]);

  // ── Edit lock — franchisee mode only ───────────────────────────────
  // Defaults to LOCKED (read-only) when opening the builder for an
  // existing franchisee. Prospect mode (no franchisee_id) stays fully
  // editable — nothing to lock. Auto-relocks after a successful save
  // so admins can't accidentally keep editing an "already saved"
  // territory. Unsaved edits trigger a beforeunload confirm.

  useEffect(() => {
    if (!(franchiseeId || isProposal) || !editMode || !dirty) return;
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [franchiseeId, isProposal, editMode, dirty]);

  // Empty-map click → resolve the sector under that lat/lng and toggle it.
  // Fixes the "I can see a white sector on the map but can't click it"
  // gap that appears when the sector sits outside the current search
  // radius (its polygon was never fetched). We top up ``sectors`` with
  // the freshly-fetched geometry so subsequent hover/label chrome works.
  const handleMapBodyClick = useCallback(async ({ lat, lng }) => {
    if (isProposal && (proposalApplied || applyingProposal)) return;
    if (franchiseeId && !editMode) return;  // locked — silently ignore
    try {
      const { data } = await api.get("/territory/sector-at-point", { params: { lat, lon: lng } });
      if (!data?.sector) return;
      setSectors((cur) => {
        if (cur.some((s) => s.sector === data.sector)) return cur;
        return [...cur, { sector: data.sector, geometry: data.geometry, district: data.district, home_count: data.home_count || 0, distance_km: 9999 }];
      });
      toggleSector(data.sector);
      setDirty(true);
    } catch (e) {
      // 404 = sea, Northern Ireland, or genuinely no sector at that point — silent.
      if (e?.response?.status !== 404) {
        console.error("[TerritoryBuilder] sector-at-point failed", e);
      }
    }
  }, [franchiseeId, editMode, toggleSector, isProposal, proposalApplied, applyingProposal]);

  // Live home count for the selected sectors (server-side authority)
  const [homeCount, setHomeCount] = useState({ count: 0, per_sector: {} });
  const [countsLoading, setCountsLoading] = useState(false);
  const [countError, setCountError] = useState("");
  const [countedCodes, setCountedCodes] = useState(null);
  const totals = useMemo(() => proposalTotals(changes, homeCount.per_sector), [changes, homeCount]);
  const countsPending = countsLoading || countedCodes !== geometryCodes;
  const displayedHomeCount = isProposal ? totals.proposed : homeCount.count;
  const proposalUnsaved = dirty || (isProposal && savedPlan && (
    JSON.stringify([...new Set(selected)].sort()) !== JSON.stringify([...new Set(savedPlan.sectors || [])].sort()) ||
    name !== (savedPlan.name || "") || notes !== (savedPlan.notes || "")
  ));
  useEffect(() => {
    if (!geometryCodes.length) {
      setHomeCount({ count: 0, per_sector: {} }); setCountedCodes(geometryCodes); setCountsLoading(false); setCountError(""); return;
    }
    setCountsLoading(true); setCountError("");
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/territory/homes-count", { params: { sectors: geometryCodes.join(",") } });
        if (!cancelled) { setHomeCount(data); setCountedCodes(geometryCodes); }
      } catch (e) {
        if (!cancelled) setCountError("Could not update home counts. Change a selection to retry.");
        console.error("[TerritoryBuilder] homes-count failed", e);
      } finally {
        if (!cancelled) setCountsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [geometryCodes]);

  const progress = Math.min(100, Math.round((displayedHomeCount / TARGET_HOMES) * 100));

  const save = async () => {
    if (isProposal && (!proposalReady || countsPending || countError || proposalApplied || applyingProposal)) return;
    if ((!selected.length && !isProposal) || (!centre && !franchiseeId && !isProposal)) { setErr("Pick a postcode and at least one sector first."); return; }
    setSaving(true); setErr("");
    try {
      if (franchiseeId) {
        // Locking a franchisee's official territory
        const { data } = await api.put(`/franchisees/${franchiseeId}/territory`, { sectors: selected });
        setTerritorySavedAt(new Date().toISOString());
        setFranchisee((cur) => cur ? { ...cur, territory_sectors: data.sectors, territory_home_count: data.home_count } : cur);
        // Loud, persistent toast so a successful save is unmissable —
        // and so the count reported by the server is the count we
        // display (catches the "I clicked save but the count looks
        // wrong" class of bugs immediately).
        setSaveToast({
          ok: true,
          message: `Territory saved · ${data.sectors.length} sector${data.sectors.length === 1 ? "" : "s"} · ${data.home_count} home${data.home_count === 1 ? "" : "s"}`,
        });
        // Successful save → auto-relock to prevent accidental follow-on
        // edits, and clear the "dirty" flag so the unsaved-changes guard
        // stops warning on nav-away.
        setEditMode(false);
        setDirty(false);
        // Re-pull the history list so the new snapshot appears.
        reloadHistory();
      } else {
        const body = {
          contact_id: contactId,
          ...(isProposal ? { plan_type: "territory_change", franchisee_id: proposalFranchiseeId, baseline_sectors: baselineSectors } : {}),
          name: name || (centreLabel ? `Territory near ${centreLabel}` : `Territory plan`),
          centre_postcode: postcode || null,
          centre_lat: centre?.lat ?? null,
          centre_lng: centre?.lng ?? null,
          sectors: selected,
          home_count: displayedHomeCount,
          notes,
          suggested_removals: suggestedRemovals,
          show_removal_overlay: showRemovalOverlay,
          show_counties: showCounties,
        };
        if (savedPlan?.id) {
          const { data } = await api.patch(`/territory-plans/${savedPlan.id}`, body);
          setSavedPlan(data);
        } else {
          const { data } = await api.post("/territory-plans", body);
          setSavedPlan(data);
        }
        setDirty(false);
        reloadAllPlans();
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Could not save";
      setErr(msg);
      setSaveToast({ ok: false, message: `Save failed · ${msg}` });
    } finally { setSaving(false); }
  };

  const applyProposal = async () => {
    if (!isProposal || !savedPlan?.id || proposalUnsaved || saving || applyingProposal || proposalApplied) return;
    const message = `Apply the agreed proposal for ${franchisee?.organisation || "this franchisee"} as their live territory?\n\n${totals.current} current − ${totals.removed} removed + ${totals.added} added = ${totals.proposed} homes.\n\nThis updates their portal map and territory allocation. The previous territory will be recorded in history.`;
    if (!window.confirm(message)) return;
    setApplyingProposal(true); setErr("");
    try {
      const { data } = await api.post(`/territory-plans/${savedPlan.id}/apply`, { confirmed: true, expected_sectors: savedPlan.sectors });
      setSavedPlan(data.plan);
      setDirty(false);
      setSaveToast({ ok: true, message: `Applied as live territory · ${data.sectors.length} sectors · ${data.home_count} homes` });
      reloadAllPlans();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not apply the proposal. Refresh and try again.");
    } finally { setApplyingProposal(false); }
  };

  // ---- contact-link helpers ------------------------------------------------
  // Lets admins attach a previously-drafted territory to a contact that just
  // landed (or move it to a different contact).
  const linkPlanToContact = async (plan, contactId) => {
    if (!plan?.id) return;
    try {
      const { data } = await api.post(`/territory-plans/${plan.id}/link-contact`, {
        contact_id: contactId || null,
      });
      // Reload the saved-plans list so the new contact_name attaches.
      await reloadAllPlans();
      if (savedPlan?.id === plan.id) setSavedPlan(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't link contact.");
    }
  };

  const deletePlan = async () => {
    if (!savedPlan?.id) return;
    if (!window.confirm("Delete this territory plan?")) return;
    await api.delete(`/territory-plans/${savedPlan.id}`);
    setSavedPlan(null);
    setSelected([]);
    setSuggestedRemovals([]);
    setShowRemovalOverlay(false);
    setName("");
    setNotes("");
    reloadAllPlans();
  };

  // ---- share link helpers -------------------------------------------------
  const [shareCopied, setShareCopied] = useState(false);
  // Ephemeral "The share link has been created, but this contact does
  // not have an email address." banner shown when the admin fires the
  // Email button on a contact with no email on file. Cleared on the
  // next share action so it never lingers past the current session.
  const [shareEmailNote, setShareEmailNote] = useState("");

  const shareUrlFor = (token) => `${window.location.origin}/share/territory/${token}`;

  const toggleShare = async (plan) => {
    if (!plan?.id) return null;
    try {
      if (plan.is_shared && plan.share_token) {
        await api.delete(`/territory-plans/${plan.id}/share`);
        const next = { ...plan, is_shared: false, share_token: null };
        if (savedPlan?.id === plan.id) setSavedPlan(next);
        setAllPlans((cur) => cur.map((p) => p.id === plan.id ? next : p));
        return null;
      }
      const { data } = await api.post(`/territory-plans/${plan.id}/share`);
      const next = { ...plan, is_shared: true, share_token: data.share_token };
      if (savedPlan?.id === plan.id) setSavedPlan(next);
      setAllPlans((cur) => cur.map((p) => p.id === plan.id ? next : p));
      return data.share_token;
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not update sharing.");
      return null;
    }
  };

  const copyShareLink = async (plan) => {
    let token = plan?.share_token;
    if (!token) {
      token = await toggleShare(plan);
      if (!token) return;
    }
    try {
      await navigator.clipboard.writeText(shareUrlFor(token));
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 1800);
    } catch (e) {
      console.warn("[TerritoryBuilder] Clipboard copy blocked", e);
    }
  };

  // "Share with prospect" — one-click flow. When the toggle goes
  // from OFF → ON, we enable sharing, wait for the share token to
  // resolve, then immediately hand off to the admin's default mail
  // app with a pre-populated draft. Going ON → OFF simply disables
  // sharing (no mail side-effect) so the admin can un-share safely.
  //
  // The mailto: hand-off is deliberately fired inside the click's
  // synchronous(ish) callback chain — modern browsers grant
  // ``window.location.href = "mailto:..."`` a user-activation pass
  // as long as it lands within the same task-queue turn as the
  // click event that triggered ``toggleShare``, which resolves
  // quickly enough in practice.
  const shareWithProspect = async (plan) => {
    setShareEmailNote("");
    // If already shared, this call turns sharing OFF — mirror the
    // pre-existing behaviour and do NOT open a new email in that
    // case (Sam doesn't want an email on un-share).
    const wasSharedBefore = !!(plan?.is_shared && plan?.share_token);
    const token = await toggleShare(plan);
    if (wasSharedBefore) return;          // was ON, we just turned it OFF
    if (!token) return;                   // enable failed → toast already surfaced
    const contactEmail = (contact?.email || "").trim();
    if (!contactEmail) {
      // Link generated fine, admin can copy manually — surface the
      // fallback copy exactly as specified.
      setShareEmailNote(NO_EMAIL_MESSAGE);
      return;
    }
    const mailto = buildProspectMailto({
      email: contactEmail,
      firstName: contact?.first_name || "",
      // ``home_count`` on the passed-in ``plan`` reflects the
      // saved plan's care-home count at the moment of sharing.
      homeCount: plan?.home_count || 0,
      shareUrl: shareUrlFor(token),
    });
    window.location.href = mailto;
  };

  const deletePlanById = async (id) => {
    if (!window.confirm("Delete this territory plan?")) return;
    try {
      await api.delete(`/territory-plans/${id}`);
      setAllPlans((cur) => cur.filter((p) => p.id !== id));
      if (savedPlan?.id === id) {
        setSavedPlan(null);
        setSelected([]);
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || "Delete failed");
    }
  };

  // ---- suggested removals overlay ---------------------------------------
  // Visual-only red-stripe overlay the admin can paint on top of selected
  // sectors when reviewing a territory with a prospective franchisee.
  // Persisted on the plan so it survives a refresh; the share link only
  // exposes it while ``showRemovalOverlay`` is true. Does NOT alter the
  // territory's actual sectors or the home count.
  const [suggestedRemovals, setSuggestedRemovals] = useState([]);
  const [showRemovalOverlay, setShowRemovalOverlay] = useState(false);
  // UK ceremonial counties overlay — off by default. Persisted with the
  // saved plan (server side) so the share link renders with the same
  // overlay state the admin last saved. Local before-save toggling
  // still works — the prop flip is instant.
  const [showCounties, setShowCounties] = useState(false);
  // Postcode-sector label toggle — ON by default because that's the
  // current behaviour. Admin can hide the label overlay when they need
  // a cleaner atlas view. Not persisted per-plan (session-only).
  const [showSectorLabels, setShowSectorLabels] = useState(true);
  // Franchisee "territory name" label overlay — OFF by default so the
  // atlas stays uncluttered. Prints "#{franchise_number} · {owner_name}"
  // on top of every franchisee polygon.
  const [showTerritoryNames, setShowTerritoryNames] = useState(false);
  const toggleRemoval = useCallback((sec) => {
    setSuggestedRemovals((cur) => (
      cur.includes(sec) ? cur.filter((s) => s !== sec) : [...cur, sec]
    ));
  }, []);

  // ---- in-map search pin -----------------------------------------------
  // Floating search box in the top-right of the map. Lets the admin
  // drop a temporary blue pin for any UK town/city or postcode sector
  // (purely visual — does NOT change the territory centre or load new
  // sectors). Clearing the search removes the pin.
  const [mapSearch, setMapSearch] = useState("");
  const [searchPin, setSearchPin] = useState(null);   // { lat, lng, label } | null
  const [searching, setSearching] = useState(false);
  const [searchErr, setSearchErr] = useState("");

  const runMapSearch = async () => {
    const q = mapSearch.trim();
    if (!q) return;
    setSearching(true);
    setSearchErr("");
    try {
      const { data } = await api.get("/territory/postcode-lookup", { params: { postcode: q } });
      if (data.latitude == null || data.longitude == null) {
        throw new Error("No match");
      }
      const label = data.matched_place
        ? `${data.matched_place} · ${data.postcode || ""}`.replace(/ · $/, "")
        : (data.postcode || q);
      setSearchPin({ lat: data.latitude, lng: data.longitude, label });
    } catch (e) {
      setSearchErr(e?.response?.data?.detail || "No UK postcode or town/city match.");
    } finally {
      setSearching(false);
    }
  };

  const clearMapSearch = () => {
    setMapSearch("");
    setSearchPin(null);
    setSearchErr("");
  };

  const sortedSelected = useMemo(() => [...selected].sort(), [selected]);

  // ---- per-panel sector filters & nearby sort ---------------------------
  // Sectors are listed in distance order for "Nearby" and alphabetically
  // for "Selected", but both panels can get long and the chip layout makes
  // visual scanning painful. A prefix filter in each panel + an A-Z toggle
  // on Nearby lets you home in on "all LE16* sectors" instantly.
  const [selectedFilter, setSelectedFilter] = useState("");
  const [nearbyFilter, setNearbyFilter] = useState("");
  const [nearbySort, setNearbySort] = useState("distance"); // "distance" | "alpha"

  const filteredSelected = useMemo(() => {
    const q = selectedFilter.trim().toUpperCase();
    if (!q) return sortedSelected;
    return sortedSelected.filter((s) => s.toUpperCase().startsWith(q));
  }, [sortedSelected, selectedFilter]);

  const filteredNearby = useMemo(() => {
    const q = nearbyFilter.trim().toUpperCase();
    let arr = q ? sectors.filter((s) => (s.sector || "").toUpperCase().startsWith(q)) : sectors;
    if (nearbySort === "alpha") {
      arr = [...arr].sort((a, b) => (a.sector || "").localeCompare(b.sector || ""));
    }
    return arr;
  }, [sectors, nearbyFilter, nearbySort]);

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-5" data-testid="territory-builder">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] font-bold text-stone-500 mb-1">
            <Target className="w-3.5 h-3.5" /> {isProposal ? "Proposed territory changes" : franchiseeId ? "Lock franchisee territory" : "Territory Builder"}
          </div>
          <h1 className="font-display text-3xl text-stone-950">
            {isProposal ? `Propose territory changes for ${franchisee?.organisation || "franchisee"}` : franchiseeId
              ? (franchisee ? `Set territory for ${franchisee.organisation || ("#" + franchisee.franchise_number)}` : "Set franchisee territory")
              : contact ? `Plan a territory for ${contact.first_name} ${contact.last_name}` : "Build a prospect territory"}
          </h1>
          {franchiseeId && (
            <Link reloadDocument to={`/territory-builder?proposal_for=${franchiseeId}`} className="inline-flex items-center gap-2 mt-3 mr-4 px-3 py-2 bg-sky-100 text-sky-900 rounded-lg text-xs font-bold" data-testid="propose-territory-changes">
              <Pencil className="w-3.5 h-3.5" /> Propose Territory Changes
            </Link>
          )}
          {isProposal && (
            <div className="mt-3 space-y-2">
              <p className="text-sm text-stone-600">Create and share proposed changes to the agreed territory.</p>
              <div className="flex flex-wrap gap-2" role="group" aria-label="Territory change tools">
                {[["remove", "Remove area"], ["add", "Add area"], ["undo", "Undo change"]].map(([mode, label]) => (
                  <button key={mode} disabled={proposalApplied || applyingProposal} onClick={() => setProposalMode(mode)} aria-pressed={proposalMode === mode} data-testid={`proposal-mode-${mode}`}
                    className={`px-3 py-2 rounded-lg text-xs font-bold border ${proposalMode === mode ? "bg-stone-900 text-white border-stone-900" : "bg-white border-stone-300"}`}>
                    {label}
                  </button>
                ))}
                <button disabled={proposalApplied || applyingProposal} onClick={() => { setSelected([...baselineSectors]); setDirty(true); }} className="px-3 py-2 rounded-lg text-xs border border-stone-300" data-testid="proposal-reset">Reset changes</button>
              </div>
              <p className="text-xs text-stone-500">{proposalMode === "remove" ? "Click a current sector to mark it red; click it again to restore green." : proposalMode === "add" ? "Click an available sector to add it in light blue; click it again to clear the addition." : "Click a changed sector to restore its original state."}</p>
            </div>
          )}
          {franchiseeId && (
            <Link to={`/franchisees/${franchiseeId}`} className="text-xs text-stone-500 hover:underline inline-flex items-center gap-1 mt-1">
              <ArrowLeft className="w-3 h-3" /> Back to franchisee
            </Link>
          )}
          {contact && !franchiseeId && (
            <Link to={`/contacts/${contact.id}`} className="text-xs text-stone-500 hover:underline inline-flex items-center gap-1 mt-1">
              <ArrowLeft className="w-3 h-3" /> Back to contact
            </Link>
          )}
          {/* Lock / Unlock — franchisee context only. Defaults to
              LOCKED so an admin has to explicitly opt into editing
              (prevents accidental clicks silently mutating a live
              territory). Auto-relocks after a successful save. */}
          {franchiseeId && (
            <div className="mt-3 inline-flex items-center gap-2" data-testid="territory-lock-pill">
              {editMode ? (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      if (dirty && !window.confirm(
                        "You have unsaved sector changes. Lock without saving? Unsaved edits will be lost.",
                      )) return;
                      setEditMode(false);
                      setDirty(false);
                      // Revert unsaved edits back to the last-saved territory.
                      if (franchisee?.territory_sectors) setSelected(franchisee.territory_sectors);
                    }}
                    data-testid="territory-lock-btn"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-wider bg-amber-500 text-white hover:bg-amber-600 rounded-full shadow-sm"
                  >
                    <Unlock className="w-3.5 h-3.5" /> Editing — click to lock
                  </button>
                  {dirty && (
                    <span className="text-[11px] text-amber-700 font-bold uppercase tracking-wider" data-testid="territory-dirty-indicator">
                      · Unsaved changes
                    </span>
                  )}
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => setEditMode(true)}
                  data-testid="territory-unlock-btn"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-wider bg-stone-200 text-stone-800 hover:bg-stone-300 rounded-full"
                >
                  <Lock className="w-3.5 h-3.5" /> Locked — click to edit
                </button>
              )}
            </div>
          )}
        </div>
        <div className="bg-white border border-stone-200 rounded-xl p-4 flex items-center gap-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-stone-500">Homes</div>
            <div className="font-display text-3xl text-stone-950 tabular-nums" data-testid="home-count">{isProposal && (countsPending || countError) ? "—" : displayedHomeCount}{!franchiseeId && !isProposal && <span className="text-stone-400 text-lg"> / {TARGET_HOMES}</span>}</div>
          </div>
          <div className="w-40">
            <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
              <div className="h-full transition-all bg-emerald-500" style={{ width: franchiseeId ? "100%" : `${progress}%` }} />
            </div>
            <div className="text-[10px] text-stone-500 mt-1">{selected.length} sector{selected.length === 1 ? "" : "s"} selected</div>
          </div>
        </div>
      </div>

      {/* Existing plans for this contact */}
      {existingPlans.length > 0 && !savedPlan && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm" data-testid="existing-plans">
          <div className="font-bold text-amber-900 mb-1">Existing plans for this contact</div>
          <div className="flex flex-wrap gap-2">
            {existingPlans.map((p) => (
              <Link key={p.id} to={`/territory-builder?contact_id=${contactId}&plan_id=${p.id}`}
                className="px-3 py-1.5 text-xs font-bold rounded-md bg-white border border-amber-300 hover:bg-amber-100">
                {p.name || "Untitled"} · {p.home_count || 0} homes
                <ChevronRight className="inline w-3 h-3 ml-0.5" />
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Search bar */}
      <div className="bg-white border border-stone-200 rounded-2xl p-4 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-1 min-w-[260px]">
          <Search className="w-4 h-4 text-stone-400" />
          <input value={postcode} onChange={(e) => setPostcode(e.target.value)} data-testid="postcode-input"
            onKeyDown={(e) => { if (e.key === "Enter") lookupPostcode(); }}
            placeholder="Postcode or UK town/city (e.g. EX15 1NB or Wokingham)"
            className="flex-1 px-2 py-1.5 text-sm bg-transparent outline-none placeholder:text-stone-400" />
          <button onClick={lookupPostcode} disabled={loading || !postcode.trim()} data-testid="lookup-postcode"
            className="px-3 py-2 text-xs font-bold uppercase tracking-wider bg-stone-950 text-white hover:bg-stone-800 rounded-lg disabled:opacity-50 flex items-center gap-1.5">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MapPin className="w-3.5 h-3.5" />}
            Drop marker
          </button>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-stone-500 font-bold uppercase tracking-wider">Radius</span>
          <input type="range" min="3" max="30" step="1" value={radiusMi}
            onChange={(e) => setRadiusMi(+e.target.value)} className="w-32" data-testid="radius-slider" />
          <span className="tabular-nums font-bold text-stone-900">{radiusMi} mi</span>
        </div>
        <button
          onClick={() => setPasteOpen(true)}
          disabled={franchiseeId && !editMode}
          data-testid="open-paste"
          className="px-3 py-2 text-xs font-bold uppercase tracking-wider bg-white border border-stone-300 text-stone-900 hover:bg-stone-50 rounded-lg flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
          title={franchiseeId && !editMode ? "Unlock the territory to paste sectors" : undefined}
        >
          <ClipboardPaste className="w-3.5 h-3.5" /> Paste sectors
        </button>
        <button
          onClick={async () => {
            setCopyingAll(true); setCopyAllStatus("");
            try {
              const { data } = await api.get("/territory/all-franchisee-sectors");
              const text = (data.sectors || []).join(", ");
              if (!text) {
                setCopyAllStatus("No live franchisee sectors found.");
              } else {
                try {
                  await navigator.clipboard.writeText(text);
                  setCopyAllStatus(`Copied ${data.count.toLocaleString()} sectors from ${data.franchisees} franchisee${data.franchisees === 1 ? "" : "s"} to your clipboard.`);
                } catch {
                  // Some browsers/contexts block writeText silently — fall
                  // back to a hidden textarea + execCommand so the admin
                  // still gets the data on their clipboard.
                  const ta = document.createElement("textarea");
                  ta.value = text;
                  ta.style.position = "fixed";
                  ta.style.left = "-9999px";
                  document.body.appendChild(ta);
                  ta.select();
                  document.execCommand("copy");
                  document.body.removeChild(ta);
                  setCopyAllStatus(`Copied ${data.count.toLocaleString()} sectors from ${data.franchisees} franchisee${data.franchisees === 1 ? "" : "s"} to your clipboard.`);
                }
              }
            } catch (e) {
              setCopyAllStatus(e?.response?.data?.detail || "Could not copy sectors.");
            } finally {
              setCopyingAll(false);
              setTimeout(() => setCopyAllStatus(""), 4000);
            }
          }}
          disabled={copyingAll}
          data-testid="copy-all-franchisee-sectors"
          title="Copy a comma-separated list of every postcode sector allocated to a live franchisee — for Facebook ads geo-targeting upload."
          className="px-3 py-2 text-xs font-bold uppercase tracking-wider bg-stone-950 hover:bg-stone-800 text-white rounded-lg flex items-center gap-1.5 disabled:opacity-50"
        >
          {copyingAll ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Copy className="w-3.5 h-3.5" />}
          Copy all franchisee postcode sectors
        </button>
      </div>

      {copyAllStatus && (
        <div className={`text-sm px-3 py-2 rounded-xl flex items-center gap-1.5 border ${
          copyAllStatus.toLowerCase().startsWith("copied")
            ? "text-emerald-800 bg-emerald-50 border-emerald-200"
            : "text-amber-800 bg-amber-50 border-amber-200"
        }`} data-testid="copy-all-status">
          {copyAllStatus.toLowerCase().startsWith("copied")
            ? <CheckCircle2 className="w-4 h-4" />
            : <AlertCircle className="w-4 h-4" />}
          {copyAllStatus}
        </div>
      )}

      {err && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-xl flex items-center gap-1.5">
          <AlertCircle className="w-4 h-4" /> {err}
        </div>
      )}

      <div className="space-y-5">
        {/* Map — full width, taller for more breathing room */}
        <div className="space-y-3">
          {/* Header strip above the map — three columns:
                · Live-franchisees toggle + legend (left)
                · Selected sectors collapsible (middle)
                · Nearby sectors collapsible (right)
              Keeps the working chips one click away without crowding the
              map below. */}
          <div className="grid lg:grid-cols-3 gap-3 items-start relative z-20">
            <div className="bg-white border border-stone-200 rounded-2xl relative">
              <div className="p-3 flex items-center gap-3 flex-wrap">
                <button
                  onClick={() => setShowOverlay((v) => !v)}
                  data-testid="toggle-franchisee-overlay"
                  className={`px-3 py-2 text-[11px] font-bold uppercase tracking-wider rounded-lg border flex items-center gap-1.5 shrink-0 ${showOverlay ? "bg-stone-950 text-white border-stone-950" : "bg-white text-stone-700 border-stone-300 hover:bg-stone-50"}`}
                  title={showOverlay ? "Hide every franchisee's territory" : "Show every franchisee's territory"}
                >
                  {showOverlay ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                  <Layers className="w-3.5 h-3.5" />
                  {overlay.franchisees.length} live franchisee{overlay.franchisees.length === 1 ? "" : "s"}
                </button>
                {showOverlay && overlay.franchisees.length > 0 && (
                  <button
                    onClick={() => setLegendOpen((v) => !v)}
                    data-testid="toggle-legend"
                    className="ml-auto text-[11px] font-bold uppercase tracking-wider text-stone-600 hover:text-stone-950 flex items-center gap-1.5 px-2 py-1.5 rounded-md hover:bg-stone-50"
                  >
                    {legendOpen ? "Hide legend" : "Show legend"}
                    {legendOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                  </button>
                )}
                {/* Atlas cache diagnostic — Phase 3. Shows freshness
                    and lets an admin manually rebuild the cache if
                    invalidation ever gets missed. */}
                <div className={`${showOverlay && overlay.franchisees.length > 0 ? "" : "ml-auto"} flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-stone-500`}>
                  {atlasAgeLabel && (
                    <span data-testid="atlas-age" title={`Atlas cached at ${atlasComputedAt}`}>
                      Atlas · {atlasAgeLabel}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={refreshAtlas}
                    disabled={atlasRefreshing}
                    data-testid="atlas-refresh"
                    title="Rebuild the shared territory atlas cache now"
                    className={`p-1 rounded-md hover:bg-stone-100 disabled:opacity-40 disabled:cursor-not-allowed`}
                  >
                    <RotateCcw className={`w-3.5 h-3.5 ${atlasRefreshing ? "animate-spin" : ""}`} />
                  </button>
                </div>
              </div>
              {showOverlay && legendOpen && overlay.franchisees.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 z-30 bg-white border border-stone-200 rounded-2xl shadow-xl p-3 grid grid-cols-2 gap-1.5 max-h-[420px] overflow-y-auto" data-testid="franchisee-legend">
                  {overlay.franchisees.map((f) => (
                    <button
                      key={f.id}
                      onClick={() => {
                        if (f.hq_lat != null && f.hq_lng != null) setCentre({ lat: f.hq_lat, lng: f.hq_lng });
                      }}
                      title={`Jump to ${f.name}${f.owner_name ? " · " + f.owner_name : ""}${f.postcode ? " · " + f.postcode : ""}`}
                      className="inline-flex items-center gap-1.5 px-2 py-1 text-[10px] font-bold rounded-md border border-stone-200 bg-stone-50 hover:bg-stone-100 min-w-0"
                      data-testid={`legend-${f.id}`}
                    >
                      <span className="w-2.5 h-2.5 rounded-full border border-white shadow-sm shrink-0" style={{ background: f.color }} />
                      <span className="truncate">{f.franchise_number ? `#${f.franchise_number} ` : ""}{f.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Selected sectors — collapsible, drops OVER the map */}
            <div className="bg-white border border-stone-200 rounded-2xl relative">
              <button
                type="button"
                onClick={() => setSelectedListOpen((v) => !v)}
                data-testid="toggle-selected-sectors"
                className="w-full flex items-center justify-between gap-3 p-4 hover:bg-stone-50 rounded-2xl"
              >
                <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-stone-500">Selected sectors ({sortedSelected.length})</span>
                {selectedListOpen ? <ChevronUp className="w-4 h-4 text-stone-500" /> : <ChevronDown className="w-4 h-4 text-stone-500" />}
              </button>
              {selectedListOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 z-30 bg-white border border-stone-200 rounded-2xl shadow-xl px-4 py-4">
                  {!sortedSelected.length ? (
                    <div className="text-xs text-stone-500">Click sectors on the map to add them here.</div>
                  ) : (
                    <>
                      {/* Prefix filter — typing "LE16" narrows to all LE16* sectors. */}
                      <div className="flex items-center gap-1.5 mb-2.5 px-2 py-1.5 bg-stone-50 border border-stone-200 rounded-lg">
                        <Search className="w-3 h-3 text-stone-400 shrink-0" />
                        <input
                          value={selectedFilter}
                          onChange={(e) => setSelectedFilter(e.target.value)}
                          placeholder="Filter (e.g. LE16)"
                          data-testid="filter-selected-sectors"
                          className="flex-1 min-w-0 bg-transparent text-[11px] outline-none placeholder:text-stone-400"
                        />
                        {selectedFilter && (
                          <button
                            type="button"
                            onClick={() => setSelectedFilter("")}
                            data-testid="filter-selected-clear"
                            className="text-stone-400 hover:text-stone-700 shrink-0"
                            aria-label="Clear filter"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-1.5 max-h-72 overflow-auto">
                        {filteredSelected.length === 0 && (
                          <div className="text-[11px] text-stone-500 italic">No selected sectors match “{selectedFilter}”.</div>
                        )}
                        {filteredSelected.map((s) => (
                          <button key={s} onClick={() => toggleSector(s)} data-testid={`chip-selected-${s}`}
                            className="group inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold uppercase tracking-wider bg-stone-950 text-white hover:bg-red-700 rounded-md">
                            {s} · {homeCount.per_sector?.[s] || 0}
                            <Trash2 className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Nearby sectors — collapsible, drops OVER the map */}
            <div className="bg-white border border-stone-200 rounded-2xl relative">
              <button
                type="button"
                onClick={() => setNearbyListOpen((v) => !v)}
                data-testid="toggle-nearby-sectors"
                className="w-full flex items-center justify-between gap-3 p-4 hover:bg-stone-50 rounded-2xl"
              >
                <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-stone-500 flex items-center gap-2">
                  Nearby sectors ({sectors.length})
                  {loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-stone-400" />}
                </span>
                {nearbyListOpen ? <ChevronUp className="w-4 h-4 text-stone-500" /> : <ChevronDown className="w-4 h-4 text-stone-500" />}
              </button>
              {nearbyListOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 z-30 bg-white border border-stone-200 rounded-2xl shadow-xl px-4 py-4">
                  {/* Filter + sort row. Sort defaults to distance (current
                      behaviour). Switching to A-Z is useful when scanning
                      for a specific outcode while ignoring radius. */}
                  <div className="flex items-center gap-2 mb-2.5">
                    <div className="flex items-center gap-1.5 flex-1 px-2 py-1.5 bg-stone-50 border border-stone-200 rounded-lg">
                      <Search className="w-3 h-3 text-stone-400 shrink-0" />
                      <input
                        value={nearbyFilter}
                        onChange={(e) => setNearbyFilter(e.target.value)}
                        placeholder="Filter (e.g. LE16)"
                        data-testid="filter-nearby-sectors"
                        className="flex-1 min-w-0 bg-transparent text-[11px] outline-none placeholder:text-stone-400"
                      />
                      {nearbyFilter && (
                        <button
                          type="button"
                          onClick={() => setNearbyFilter("")}
                          data-testid="filter-nearby-clear"
                          className="text-stone-400 hover:text-stone-700 shrink-0"
                          aria-label="Clear filter"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                    <div className="flex items-center bg-stone-50 border border-stone-200 rounded-lg p-0.5" role="tablist" aria-label="Sort nearby sectors">
                      <button
                        type="button"
                        onClick={() => setNearbySort("distance")}
                        data-testid="sort-nearby-distance"
                        aria-pressed={nearbySort === "distance"}
                        className={`px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded-md ${nearbySort === "distance" ? "bg-stone-950 text-white" : "text-stone-600 hover:text-stone-900"}`}
                      >
                        Distance
                      </button>
                      <button
                        type="button"
                        onClick={() => setNearbySort("alpha")}
                        data-testid="sort-nearby-alpha"
                        aria-pressed={nearbySort === "alpha"}
                        className={`px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded-md ${nearbySort === "alpha" ? "bg-stone-950 text-white" : "text-stone-600 hover:text-stone-900"}`}
                      >
                        A–Z
                      </button>
                    </div>
                  </div>
                  <div className="max-h-72 overflow-auto space-y-1">
                    {filteredNearby.length === 0 && (
                      <div className="text-[11px] text-stone-500 italic px-1">No nearby sectors match “{nearbyFilter}”.</div>
                    )}
                    {filteredNearby.map((s) => {
                      const isSel = selected.includes(s.sector);
                      return (
                        <button key={s.sector} onClick={() => toggleSector(s.sector)} data-testid={`chip-near-${s.sector}`}
                          className={`w-full flex items-center justify-between gap-2 px-2.5 py-1.5 text-xs rounded-lg text-left ${isSel ? "bg-stone-950 text-white" : "hover:bg-stone-50 text-stone-800"}`}>
                          <span className="font-bold">{s.sector}</span>
                          <span className="tabular-nums">{s.home_count} homes · {(s.distance_km / KM_PER_MI).toFixed(1)} mi</span>
                          {isSel ? <CheckCircle2 className="w-3.5 h-3.5 text-[#dddd16] shrink-0" /> : <Plus className="w-3.5 h-3.5 text-stone-400 shrink-0" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="relative">
            <TerritoryMap
              sectors={isProposal ? sectors.map((s) => ({ ...s, home_count: homeCount.per_sector?.[s.sector] ?? s.home_count })) : sectors}
              selected={selected}
              centre={centre}
              centreLabel={centreLabel}
              onToggleSector={toggleSector}
              onMapClick={handleMapBodyClick}
              height={mapTall ? 1200 : 820}
              franchiseeOverlay={showOverlay ? overlay : null}
              searchPin={searchPin}
              suggestedRemovals={showRemovalOverlay ? suggestedRemovals : []}
              territoryChanges={isProposal ? changes : null}
              overlayMode={!isProposal && showRemovalOverlay}
              onToggleRemoval={toggleRemoval}
              showCounties={showCounties}
              showSectorLabels={showSectorLabels}
              showTerritoryNames={showTerritoryNames}
              savedPlansOverlay={overlayPlansData}
            />
            <button
              type="button"
              onClick={() => setMapTall((v) => !v)}
              data-testid="toggle-map-height"
              aria-label={mapTall ? "Shrink map to 820px" : "Expand map to 1200px"}
              title={mapTall ? "Shrink map" : "Expand map"}
              className="absolute top-3 left-3 z-10 w-9 h-9 rounded-full bg-white/95 backdrop-blur border border-stone-300 shadow-lg flex items-center justify-center text-stone-800 hover:bg-stone-950 hover:text-white transition-colors"
            >
              {mapTall ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            {/* Floating search box — sits over the top-right corner of
                the map. Purpose: lets the admin drop a temporary blue
                pin for any UK town or postcode sector when scoping out
                where a prospective franchisee says they'd like to be
                based. Does NOT change the territory centre or reload
                sectors — purely a visual guide. */}
            <div
              className="absolute top-3 right-14 z-10 bg-white/95 backdrop-blur border border-stone-300 rounded-xl shadow-lg p-2 flex items-center gap-1.5"
              style={{ width: "min(330px, calc(100% - 88px))" }}
              data-testid="map-search-box"
            >
              <Search className="w-3.5 h-3.5 text-stone-500 shrink-0 ml-1" />
              <input
                value={mapSearch}
                onChange={(e) => { setMapSearch(e.target.value); if (searchErr) setSearchErr(""); }}
                onKeyDown={(e) => { if (e.key === "Enter") runMapSearch(); }}
                placeholder="Search town or postcode (e.g. Bath, RG40 1)"
                data-testid="map-search-input"
                className="flex-1 min-w-0 px-1.5 py-1.5 text-xs bg-transparent outline-none placeholder:text-stone-400"
              />
              {(mapSearch || searchPin) && (
                <button
                  type="button"
                  onClick={clearMapSearch}
                  data-testid="map-search-clear"
                  className="px-2 py-1.5 text-stone-500 hover:text-stone-900 hover:bg-stone-100 rounded-md shrink-0"
                  title="Clear search and remove pin"
                  aria-label="Clear search"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                </button>
              )}
              <button
                type="button"
                onClick={runMapSearch}
                disabled={searching || !mapSearch.trim()}
                data-testid="map-search-go"
                className="px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wider bg-stone-950 text-white hover:bg-stone-800 disabled:opacity-40 rounded-md shrink-0 flex items-center gap-1"
              >
                {searching ? <Loader2 className="w-3 h-3 animate-spin" /> : <MapPin className="w-3 h-3" />}
                Drop pin
              </button>
            </div>
            {searchErr && (
              <div
                className="absolute top-16 right-14 z-10 bg-amber-50/95 backdrop-blur border border-amber-300 text-amber-900 text-[11px] px-3 py-1.5 rounded-lg shadow-sm flex items-center gap-1.5"
                style={{ width: "min(330px, calc(100% - 88px))" }}
                data-testid="map-search-error"
              >
                <AlertCircle className="w-3 h-3 shrink-0" />
                <span className="flex-1">{searchErr}</span>
              </div>
            )}
            {/* Overlay toggles cluster — bottom-right of the map,
                admin-only. Postcode overlay hides the "SW15 5 · N homes"
                labels; Territory Name overlays "#123 · Franchisee" on
                each franchisee territory; Counties adds UK ceremonial
                borders. All three float above the Mapbox scale ctrl. */}
            <div className="absolute bottom-3 right-3 z-10 flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => setShowSectorLabels((v) => !v)}
                data-testid="toggle-sector-labels"
                title={showSectorLabels ? "Hide postcode-sector labels" : "Show postcode-sector labels"}
                className={`px-3 py-2 rounded-lg text-[10px] font-bold uppercase tracking-wider shadow-lg border transition-colors flex items-center gap-1.5 ${
                  showSectorLabels
                    ? "bg-teal-600 text-white border-teal-700 hover:bg-teal-700"
                    : "bg-white/95 backdrop-blur text-stone-800 border-stone-300 hover:bg-stone-50"
                }`}
              >
                <Layers className="w-3.5 h-3.5" />
                Postcode Overlay
                <span className={`px-1.5 py-0.5 rounded-md text-[9px] tabular-nums ${showSectorLabels ? "bg-white/25 text-white" : "bg-stone-100 text-stone-500"}`}>
                  {showSectorLabels ? "ON" : "OFF"}
                </span>
              </button>
              <button
                type="button"
                onClick={() => setShowTerritoryNames((v) => !v)}
                data-testid="toggle-territory-names"
                title={showTerritoryNames ? "Hide franchisee territory names" : "Show franchisee territory names"}
                className={`px-3 py-2 rounded-lg text-[10px] font-bold uppercase tracking-wider shadow-lg border transition-colors flex items-center gap-1.5 ${
                  showTerritoryNames
                    ? "bg-fuchsia-700 text-white border-fuchsia-800 hover:bg-fuchsia-800"
                    : "bg-white/95 backdrop-blur text-stone-800 border-stone-300 hover:bg-stone-50"
                }`}
              >
                <Layers className="w-3.5 h-3.5" />
                Territory Name
                <span className={`px-1.5 py-0.5 rounded-md text-[9px] tabular-nums ${showTerritoryNames ? "bg-white/25 text-white" : "bg-stone-100 text-stone-500"}`}>
                  {showTerritoryNames ? "ON" : "OFF"}
                </span>
              </button>
              {/* County boundaries overlay toggle — floats bottom-right of
                  the map, above the Mapbox scale control. State is stored
                  on the plan (server side) so the same toggle applies to
                  the public share link. */}
              <button
                type="button"
                onClick={() => setShowCounties((v) => !v)}
                data-testid="toggle-counties"
                title={showCounties ? "Hide UK county boundaries" : "Show UK county boundaries"}
                className={`px-3 py-2 rounded-lg text-[10px] font-bold uppercase tracking-wider shadow-lg border transition-colors flex items-center gap-1.5 ${
                  showCounties
                    ? "bg-indigo-600 text-white border-indigo-700 hover:bg-indigo-700"
                    : "bg-white/95 backdrop-blur text-stone-800 border-stone-300 hover:bg-stone-50"
                }`}
              >
                <Layers className="w-3.5 h-3.5" />
                Counties
                <span className={`px-1.5 py-0.5 rounded-md text-[9px] tabular-nums ${showCounties ? "bg-white/25 text-white" : "bg-stone-100 text-stone-500"}`}>
                  {showCounties ? "ON" : "OFF"}
                </span>
              </button>
            </div>
          </div>
          {/* Live homes-count bar — sits directly below the map so the
              number is in the user's eyeline as they click sectors. Sticky
              to the bottom of the viewport so it stays visible even while
              scrolling through nearby-sectors lists. */}
          <div
            data-testid="live-homes-bar"
            className="sticky bottom-3 z-20 mt-2 bg-white border-2 border-stone-950 rounded-xl shadow-lg px-4 py-2.5 flex items-center gap-4 flex-wrap"
          >
            <div className="flex items-baseline gap-2">
              <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-stone-500">Homes</div>
              <div className="font-display text-2xl text-stone-950 tabular-nums leading-none" data-testid="home-count-live">
                {isProposal && (countsPending || countError) ? "—" : displayedHomeCount}
                {!franchiseeId && !isProposal && <span className="text-stone-400 text-base"> / {TARGET_HOMES}</span>}
              </div>
            </div>
            <div className="flex-1 min-w-[140px] max-w-xs">
              <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all ${
                    franchiseeId ? "bg-emerald-500"
                    : homeCount.count >= TARGET_HOMES ? "bg-emerald-500"
                    : homeCount.count >= TARGET_HOMES * 0.8 ? "bg-amber-400"
                    : "bg-stone-400"
                  }`}
                  style={{ width: (franchiseeId || isProposal) ? "100%" : `${progress}%` }}
                />
              </div>
              <div className="text-[10px] text-stone-500 mt-1 tabular-nums">
                {selected.length} sector{selected.length === 1 ? "" : "s"} selected
                {!franchiseeId && !isProposal && homeCount.count > 0 && (
                  <> · {homeCount.count >= TARGET_HOMES
                    ? <span className="text-emerald-700 font-bold">target met</span>
                    : <span>{TARGET_HOMES - homeCount.count} to go</span>}</>
                )}
              </div>
            </div>
          </div>
          <div className="text-[11px] text-stone-500 mt-2 flex items-center gap-3 flex-wrap">
            {!isProposal && <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[#dddd16] border border-[#14532D]" /> Selected sector</span>}
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-stone-200 border border-stone-400" /> Available sector</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-red-500 border-2 border-white shadow" /> Contact's postcode</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-indigo-500 border-2 border-white shadow" /> Existing franchisee HQ</span>
            <span className="text-stone-400">·</span>
            <span>Click a coloured area or pin to identify the franchisee</span>
          </div>
        </div>

        {isProposal && <TerritoryProposalSummary applied={proposalApplied} totals={totals} loading={countsPending} error={countError} />}

        {/* Below-map panels — Plan Details on the left, Saved Plans on
            the right (when not in contact/franchisee context). */}
        <div className="grid lg:grid-cols-3 gap-4 items-start">
          <div className="bg-white border border-stone-200 rounded-2xl p-4 lg:col-span-1">
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-stone-500 mb-2">{franchiseeId ? "Franchisee territory" : "Plan details"}</div>
            {!franchiseeId && (
              <>
                <input disabled={isProposal && (proposalApplied || applyingProposal)} value={name} onChange={(e) => { setName(e.target.value); if (isProposal) setDirty(true); }} data-testid="plan-name"
                  placeholder="Plan name (e.g. Exeter & Mid Devon proposal)"
                  className="w-full px-3 py-2 text-sm bg-stone-50 border border-stone-200 rounded-lg mb-2" />
                <textarea disabled={isProposal && (proposalApplied || applyingProposal)} value={notes} onChange={(e) => { setNotes(e.target.value); if (isProposal) setDirty(true); }} data-testid="plan-notes"
                  rows={3} placeholder={isProposal ? "Notes shown on the share link (optional)" : "Notes (optional)"}
                  className="w-full px-3 py-2 text-sm bg-stone-50 border border-stone-200 rounded-lg" />
                {/* Suggested removals overlay — visual-only red-stripe
                    layer over selected sectors. When ON, clicking the
                    map paints/clears the flag on a sector. Toggling
                    OFF hides the layer (but preserves the flags) and
                    removes it from the share-link view. Does not
                    change the territory's selected sectors or counts. */}
                {!isProposal && <div className="mt-3 px-3 py-2.5 bg-stone-50 border border-stone-200 rounded-lg">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showRemovalOverlay}
                      onChange={(e) => setShowRemovalOverlay(e.target.checked)}
                      data-testid="toggle-removal-overlay"
                      className="mt-0.5 accent-red-600"
                    />
                    <div className="flex-1">
                      <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-stone-700 flex items-center gap-2">
                        Allow edit overlay
                        {showRemovalOverlay && (
                          <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-red-600 text-white rounded">On</span>
                        )}
                      </div>
                      <div className="text-[11px] text-stone-500 mt-0.5 leading-snug">
                        Paint sectors with a red-stripe overlay to suggest areas to drop. Visual only — won't change selection or counts. Visible on the share link while on.
                      </div>
                      {showRemovalOverlay && suggestedRemovals.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setSuggestedRemovals([])}
                          data-testid="clear-removal-overlay"
                          className="mt-2 text-[10px] font-bold uppercase tracking-wider text-red-700 hover:text-red-900"
                        >
                          Clear {suggestedRemovals.length} flagged
                        </button>
                      )}
                    </div>
                  </label>
                </div>}
                {/* County boundaries — persisted on the plan so the same
                    toggle applies to the public share link. Mirror of the
                    map's floating "Counties" button, exposed here so it's
                    obvious that the setting travels with the shared plan. */}
                <div className="mt-2 px-3 py-2.5 bg-stone-50 border border-stone-200 rounded-lg">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showCounties}
                      onChange={(e) => setShowCounties(e.target.checked)}
                      data-testid="toggle-counties-plan"
                      className="mt-0.5 accent-indigo-600"
                    />
                    <div className="flex-1">
                      <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-stone-700 flex items-center gap-2">
                        Show county boundaries
                        {showCounties && (
                          <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-indigo-600 text-white rounded">On</span>
                        )}
                      </div>
                      <div className="text-[11px] text-stone-500 mt-0.5 leading-snug">
                        Overlay UK ceremonial county names on the map. Helps prospects orient the proposed territory. Visible on the share link while on.
                      </div>
                    </div>
                  </label>
                </div>
              </>
            )}
            {franchiseeId && (
              <div className="text-xs text-stone-600 leading-relaxed">
                Pick every postcode sector that belongs to this franchisee. Saving overwrites their official territory — they'll see it on their portal map immediately, and the public website lookup will route those postcodes here.
              </div>
            )}
            <div className="flex items-center gap-2 mt-3">
              <button onClick={save} disabled={saving || (!selected.length && !isProposal) || (franchiseeId && !editMode) || (isProposal && (!proposalReady || countsPending || !!countError || proposalApplied || applyingProposal))} data-testid="save-plan"
                className="flex-1 px-3 py-2 text-xs font-bold uppercase tracking-wider bg-[#dddd16] text-stone-950 hover:bg-[#aaaa11] rounded-lg disabled:opacity-50 flex items-center justify-center gap-1.5"
                title={franchiseeId && !editMode ? "Unlock the territory to save changes" : undefined}>
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                {franchiseeId ? "Save territory" : isProposal ? (savedPlan ? "Update proposal" : "Save proposal") : (savedPlan ? "Update plan" : "Save plan")}
              </button>
              {savedPlan && !franchiseeId && (
                <button disabled={proposalApplied || applyingProposal} onClick={deletePlan} className="px-3 py-2 text-xs font-bold rounded-lg border border-red-300 text-red-700 hover:bg-red-50" data-testid="delete-plan">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
              <button
                onClick={() => { setSelected(isProposal ? [...baselineSectors] : []); if (franchiseeId || isProposal) setDirty(true); }}
                disabled={(franchiseeId && !editMode) || (isProposal && (proposalApplied || applyingProposal))}
                className="px-3 py-2 text-xs font-bold rounded-lg border border-stone-300 hover:bg-stone-50 disabled:opacity-40 disabled:cursor-not-allowed"
                title={franchiseeId && !editMode ? "Unlock the territory to clear sectors" : "Clear all sectors"}>
                <RotateCcw className="w-3.5 h-3.5" />
              </button>
            </div>
            {savedPlan && !franchiseeId && (
              <div className="mt-3 px-3 py-2 text-[11px] bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5" /> Saved · last update {new Date(savedPlan.updated_at || savedPlan.created_at).toLocaleString()}
              </div>
            )}
            {isProposal && savedPlan && (
              <div className="mt-3 p-3 border border-emerald-200 bg-emerald-50 rounded-lg" data-testid="apply-proposal-controls">
                {proposalApplied ? (
                  <p className="text-xs text-emerald-900">Applied as live territory on {new Date(savedPlan.applied_at).toLocaleString()}.
                    <Link reloadDocument to={`/territory-builder?franchisee_id=${proposalFranchiseeId}`} className="block mt-2 font-bold underline">View live territory and history</Link>
                  </p>
                ) : (
                  <>
                    <button onClick={applyProposal} disabled={proposalUnsaved || saving || applyingProposal || countsPending || !!countError || !selected.length || !(changes.removed.length + changes.added.length)}
                      className="w-full px-3 py-2 bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-bold rounded-lg disabled:opacity-50"
                      data-testid="apply-proposal">{applyingProposal ? "Applying…" : "Apply as live territory"}</button>
                    <p className="mt-2 text-xs text-emerald-900">{proposalUnsaved ? "Save your changes first." : "Once agreed with the franchisee, apply this saved proposal to their live territory."}</p>
                  </>
                )}
              </div>
            )}

            {/* Share link controls — only when a plan is saved. The admin
                toggles sharing on/off; while on, "Copy link" places a
                /share/territory/<token> URL on the clipboard for prospects. */}
            {savedPlan && !franchiseeId && (
              <div className="mt-2 px-3 py-2 border border-stone-200 bg-stone-50 rounded-lg space-y-2" data-testid="share-controls">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 text-[11px] font-bold text-stone-700">
                    <Share2 className="w-3.5 h-3.5" />
                    {isProposal ? "Share with franchisee" : "Share with prospect"}
                  </div>
                  <button
                    onClick={() => isProposal ? toggleShare(savedPlan) : shareWithProspect(savedPlan)}
                    disabled={isProposal && dirty && !savedPlan.is_shared}
                    title={isProposal && dirty ? "Save your changes before sharing" : undefined}
                    data-testid="share-toggle"
                    className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full transition-colors ${
                      savedPlan.is_shared
                        ? "bg-emerald-600 text-white hover:bg-emerald-700"
                        : "bg-stone-200 text-stone-700 hover:bg-stone-300"
                    }`}
                  >
                    {savedPlan.is_shared ? "On" : "Off"}
                  </button>
                </div>
                {savedPlan.is_shared && savedPlan.share_token && (
                  <>
                    <div className="flex items-center gap-1.5">
                      <input
                        readOnly
                        value={shareUrlFor(savedPlan.share_token)}
                        data-testid="share-url"
                        onFocus={(e) => e.target.select()}
                        className="flex-1 px-2 py-1 text-[11px] font-mono bg-white border border-stone-300 rounded text-stone-700"
                      />
                      <button
                        onClick={() => copyShareLink(savedPlan)}
                        data-testid="share-copy"
                        className="px-2 py-1 text-[10px] font-bold rounded border border-stone-300 bg-white hover:bg-stone-50 flex items-center gap-1"
                        title="Copy link"
                      >
                        <Copy className="w-3 h-3" />
                        {shareCopied ? "Copied" : "Copy"}
                      </button>
                    </div>
                    {shareEmailNote ? (
                      <div
                        className="text-[10px] font-medium text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1"
                        data-testid="share-email-no-address"
                        role="status"
                      >
                        {shareEmailNote}
                      </div>
                    ) : null}
                    <div className="text-[10px] text-stone-500">
                      Anyone with this link can view the territory (no login).
                      {savedPlan.view_count ? ` · Opened ${savedPlan.view_count}×` : ""}
                    </div>
                  </>
                )}
                {!savedPlan.is_shared && (
                  <div className="text-[10px] text-stone-500">
                    {isProposal && dirty && <span>Save your changes first. </span>}
                    Turn on to generate a public read-only link.
                  </div>
                )}
              </div>
            )}
            {franchiseeId && territorySavedAt && (
              <div className="mt-3 px-3 py-2 text-[11px] bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5" /> Locked · {new Date(territorySavedAt).toLocaleString()}
              </div>
            )}

            {/* Save toast — explicit success/failure feedback right next
                to the Lock button. Auto-dismisses on success after 6s. */}
            {saveToast && (
              <div
                data-testid="territory-save-toast"
                className={`mt-2 px-3 py-2 text-xs rounded-lg flex items-start gap-2 ${
                  saveToast.ok
                    ? "bg-emerald-100 border border-emerald-300 text-emerald-900"
                    : "bg-red-100 border border-red-300 text-red-900"
                }`}
              >
                {saveToast.ok ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" /> : <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />}
                <div className="flex-1 font-semibold">{saveToast.message}</div>
                <button onClick={() => setSaveToast(null)} className="text-current opacity-70 hover:opacity-100" aria-label="Dismiss">×</button>
              </div>
            )}

            {/* Audit / Rollback history — every save snapshots into
                territory_history, so an accidental save is one click away
                from being undone. Compact, expandable, scoped to this
                franchisee. */}
            {franchiseeId && (
              <div className="mt-3 border border-stone-200 rounded-lg overflow-hidden" data-testid="territory-history">
                <button
                  onClick={() => { setHistoryOpen((v) => !v); if (!historyOpen) reloadHistory(); }}
                  className="w-full px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-stone-700 bg-stone-50 hover:bg-stone-100 flex items-center justify-between gap-2"
                  data-testid="territory-history-toggle"
                >
                  <span className="flex items-center gap-1.5">
                    <History className="w-3.5 h-3.5" />
                    Save history {history.length > 0 && <span className="text-stone-500">({history.length})</span>}
                  </span>
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${historyOpen ? "rotate-180" : ""}`} />
                </button>
                {historyOpen && (
                  <div className="divide-y divide-stone-100 max-h-72 overflow-y-auto">
                    {history.length === 0 && (
                      <div className="px-3 py-3 text-[11px] text-stone-500 italic">
                        No prior saves recorded yet. From now on, every Lock Territory click leaves a recoverable snapshot here.
                      </div>
                    )}
                    {history.map((h) => (
                      <div key={h.id} className="px-3 py-2 text-[11px]" data-testid={`territory-history-row-${h.id}`}>
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-bold text-stone-900">
                            {new Date(h.changed_at).toLocaleString()}
                          </div>
                          <button
                            onClick={() => rollbackTo(h)}
                            disabled={rollingBack === h.id}
                            data-testid={`territory-rollback-${h.id}`}
                            className="px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded bg-stone-950 text-[#dddd16] hover:bg-stone-800 disabled:opacity-50 flex items-center gap-1"
                          >
                            <RotateCcw className="w-2.5 h-2.5" /> {rollingBack === h.id ? "…" : "Restore"}
                          </button>
                        </div>
                        <div className="text-stone-600 mt-0.5">
                          {h.changed_by || "system"} · {(h.previous_sectors?.length ?? 0)} → {(h.new_sectors?.length ?? 0)} sectors
                          {h.added_count > 0 && <span className="text-emerald-700"> · +{h.added_count}</span>}
                          {h.removed_count > 0 && <span className="text-red-700"> · −{h.removed_count}</span>}
                          {h.rollback_from && <span className="ml-1 px-1 py-0.5 rounded bg-stone-200 text-stone-700 font-bold">ROLLBACK</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Saved territory plans — sits in the same grid row as Plan
              Details so admins can see both at a glance. Spans 2/3 of
              the width on large screens. */}
          {!contactId && !franchiseeId && (
            <div className="lg:col-span-2">
              <SavedPlansPanel
                plans={allPlans}
                loading={allPlansLoading}
                filter={planFilter}
                onFilter={setPlanFilter}
                activeId={savedPlan?.id || planId}
                onCopyShare={copyShareLink}
                onToggleShare={toggleShare}
                onDelete={deletePlanById}
                onLinkContact={linkPlanToContact}
                shareUrlFor={shareUrlFor}
                shareCopied={shareCopied}
                overlayPlanIds={overlayPlanIds}
                onToggleOverlay={toggleOverlayPlan}
              />
            </div>
          )}
        </div>
      </div>

      {/* Paste-sectors modal */}
      {pasteOpen && (
        <div onClick={() => setPasteOpen(false)} className="fixed inset-0 z-50 bg-stone-950/70 backdrop-blur-sm flex items-center justify-center p-6" data-testid="paste-modal">
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full">
            <div className="px-5 py-4 border-b border-stone-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ClipboardPaste className="w-4 h-4 text-stone-700" />
                <span className="font-bold text-stone-950">Paste postcode sectors</span>
              </div>
              <button onClick={() => setPasteOpen(false)} className="w-8 h-8 hover:bg-stone-100 rounded-md flex items-center justify-center">
                <RotateCcw className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-xs text-stone-600">Paste a list of postcode sectors — one per line, comma- or space-separated all work. e.g. <code className="bg-stone-100 px-1 rounded">BA7 7</code>, <code className="bg-stone-100 px-1 rounded">BA20 1</code>, <code className="bg-stone-100 px-1 rounded">DT9 4</code>…</p>
              <textarea value={pasteText} onChange={(e) => setPasteText(e.target.value)} data-testid="paste-textarea"
                rows={10} placeholder="BA7 7&#10;BA20 1&#10;BA20 2&#10;DT1 1&#10;…"
                className="w-full px-3 py-2 text-sm font-mono bg-stone-50 border border-stone-200 rounded-lg" />
              {pastePreview && (
                <div className="text-xs space-y-1">
                  <div className="text-emerald-700">
                    <strong>{pastePreview.sectors.length}</strong> sectors recognised · <strong>{pastePreview.home_count}</strong> homes
                  </div>
                  {pastePreview.unrecognised.length > 0 && (
                    <div className="text-amber-700">
                      <strong>{pastePreview.unrecognised.length}</strong> not recognised: {pastePreview.unrecognised.slice(0, 6).join(", ")}{pastePreview.unrecognised.length > 6 ? "…" : ""}
                    </div>
                  )}
                </div>
              )}
              <div className="flex items-center justify-end gap-2 pt-2">
                <button onClick={() => setPasteOpen(false)} className="px-3 py-2 text-xs font-bold rounded-lg border border-stone-300 hover:bg-stone-50">Cancel</button>
                <button data-testid="paste-preview"
                  onClick={async () => {
                    try {
                      const { data } = await api.post(`/franchisees/${franchiseeId || "preview"}/territory/parse`, { text: pasteText });
                      setPastePreview(data);
                    } catch (e) {
                      setPastePreview({ sectors: [], unrecognised: [], home_count: 0, error: e?.response?.data?.detail || "Parse failed" });
                    }
                  }}
                  className="px-3 py-2 text-xs font-bold rounded-lg border border-stone-400 hover:bg-stone-50">
                  Preview
                </button>
                <button data-testid="paste-apply"
                  onClick={async () => {
                    let toApply = pastePreview?.sectors;
                    if (!toApply) {
                      try {
                        const { data } = await api.post(`/franchisees/${franchiseeId || "preview"}/territory/parse`, { text: pasteText });
                        toApply = data.sectors;
                      } catch (e) {
                        setErr(e?.response?.data?.detail || "Parse failed");
                        return;
                      }
                    }
                    if (!toApply?.length || (isProposal && (proposalApplied || applyingProposal))) return;
                    if (isProposal) setDirty(true);
                    setSelected((cur) => {
                      const set = new Set([...cur, ...toApply]);
                      return [...set];
                    });
                    setPasteOpen(false);
                    setPasteText("");
                    setPastePreview(null);
                  }}
                  className="px-3 py-2 text-xs font-bold uppercase tracking-wider rounded-lg bg-[#dddd16] text-stone-950 hover:bg-[#aaaa11]">
                  Add to selection
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// -------------------------------- Saved plans panel ---------------------
// Lists every territory plan the admin has saved, with quick actions:
// Open (loads it onto the current map), Copy share link, toggle share,
// Delete. Filter box narrows by plan name / contact / centre postcode.
function SavedPlansPanel({
  plans, loading, filter, onFilter, activeId, onCopyShare, onToggleShare,
  onDelete, onLinkContact, shareUrlFor, shareCopied,
  overlayPlanIds, onToggleOverlay,
}) {
  const [pickerForPlanId, setPickerForPlanId] = useState(null);
  const [contactQuery, setContactQuery] = useState("");
  const [contactResults, setContactResults] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);

  // Debounced contact search — fires once a picker is open.
  useEffect(() => {
    if (!pickerForPlanId) return;
    if (!contactQuery || contactQuery.trim().length < 2) { setContactResults([]); return; }
    const t = setTimeout(async () => {
      setContactsLoading(true);
      try {
        const { data } = await api.get("/contacts", {
          params: { search: contactQuery.trim(), limit: 15 },
        });
        const items = data.items || data.contacts || (Array.isArray(data) ? data : []);
        setContactResults(items);
      } catch (e) {
        console.error("[SavedPlansPanel] contact search failed", e);
        setContactResults([]);
      } finally { setContactsLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [contactQuery, pickerForPlanId]);

  const openPicker = (planId) => {
    setPickerForPlanId(planId);
    setContactQuery("");
    setContactResults([]);
  };
  const closePicker = () => setPickerForPlanId(null);
  const pick = async (plan, contactId) => {
    await onLinkContact(plan, contactId);
    closePicker();
  };

  const filtered = (plans || []).filter((p) => {
    if (!filter.trim()) return true;
    const q = filter.toLowerCase();
    return (
      (p.name || "").toLowerCase().includes(q)
      || (p.contact_name || "").toLowerCase().includes(q)
      || (p.centre_postcode || "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-4" data-testid="saved-plans-panel">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-stone-500 flex items-center gap-1.5">
          <FolderOpen className="w-3.5 h-3.5" />
          Saved plans ({plans.length})
        </div>
        {loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-stone-400" />}
      </div>

      {plans.length > 5 && (
        <input
          value={filter}
          onChange={(e) => onFilter(e.target.value)}
          placeholder="Filter by name / contact / postcode"
          data-testid="saved-plans-filter"
          className="w-full px-2.5 py-1.5 mb-2 text-xs bg-stone-50 border border-stone-200 rounded-lg"
        />
      )}

      {!plans.length && !loading && (
        <div className="text-xs text-stone-500 leading-relaxed">
          No saved plans yet. Build a territory, give it a name, then click <strong>Save plan</strong>. Saved plans appear here so you can re-open or share them with prospects.
        </div>
      )}

      <div className="max-h-[28rem] overflow-auto grid grid-cols-1 md:grid-cols-2 gap-1.5" data-testid="saved-plans-list">
        {filtered.map((p) => {
          const isActive = p.id === activeId;
          return (
            <div
              key={p.id}
              data-testid={`saved-plan-${p.id}`}
              className={`group rounded-lg border px-2.5 py-2 transition ${
                isActive
                  ? "border-emerald-400 bg-emerald-50/50"
                  : "border-stone-200 hover:border-stone-300 bg-white"
              }`}
            >
              <Link
                to={`/territory-builder?plan_id=${p.id}${p.contact_id ? `&contact_id=${p.contact_id}` : ""}`}
                data-testid={`saved-plan-open-${p.id}`}
                className="block"
              >
                <div className="text-xs font-bold text-stone-950 truncate flex items-center gap-1.5">
                  {p.name || "Untitled plan"}
                  {isActive && <CheckCircle2 className="w-3 h-3 text-emerald-600" />}
                </div>
                <div className="text-[10px] text-stone-500 truncate mt-0.5">
                  {p.contact_name && <span>{p.contact_name} · </span>}
                  <span className="tabular-nums">{p.home_count || 0} homes · {(p.sectors || []).length} sectors</span>
                  {p.centre_postcode && <span> · {p.centre_postcode}</span>}
                </div>
              </Link>
              <div className="mt-1.5 flex items-center gap-1 flex-wrap">
                {onToggleOverlay && (
                  <button
                    type="button"
                    onClick={() => onToggleOverlay(p.id)}
                    data-testid={`saved-plan-overlay-${p.id}`}
                    aria-pressed={overlayPlanIds?.has(p.id) || false}
                    title={overlayPlanIds?.has(p.id) ? "Hide this potential area from the map" : "Show this potential area on the map"}
                    className={`px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded flex items-center gap-1 border ${
                      overlayPlanIds?.has(p.id)
                        ? "border-amber-500 bg-amber-500 text-white hover:bg-amber-600"
                        : "border-stone-300 text-stone-700 hover:bg-stone-50"
                    }`}
                  >
                    {overlayPlanIds?.has(p.id) ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                    {overlayPlanIds?.has(p.id) ? "On map" : "Show"}
                  </button>
                )}
                <button
                  onClick={() => openPicker(p.id)}
                  data-testid={`saved-plan-link-${p.id}`}
                  className={`px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded flex items-center gap-1 ${
                    p.contact_id
                      ? "border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                      : "border border-stone-300 hover:bg-stone-50 text-stone-700"
                  }`}
                  title={p.contact_id ? `Linked to ${p.contact_name || "a contact"} — click to change or unlink` : "Link this plan to a contact"}
                >
                  <Users className="w-3 h-3" />
                  {p.contact_id ? "Linked" : "Link contact"}
                </button>
                <button
                  onClick={() => onCopyShare(p)}
                  data-testid={`saved-plan-share-${p.id}`}
                  className="flex-1 px-2 py-1 text-[10px] font-bold uppercase tracking-wider border border-stone-300 hover:bg-stone-50 rounded flex items-center justify-center gap-1"
                  title={p.is_shared ? "Copy share link" : "Generate and copy share link"}
                >
                  {p.is_shared ? <LinkIcon className="w-3 h-3" /> : <Share2 className="w-3 h-3" />}
                  {p.is_shared
                    ? (shareCopied ? "Copied" : "Copy link")
                    : "Share"}
                </button>
                {p.is_shared && (
                  <button
                    onClick={() => onToggleShare(p)}
                    data-testid={`saved-plan-unshare-${p.id}`}
                    className="px-2 py-1 text-[10px] font-bold rounded border border-stone-300 hover:bg-stone-50 text-stone-600"
                    title="Revoke share link"
                  >
                    <EyeOff className="w-3 h-3" />
                  </button>
                )}
                <button
                  onClick={() => onDelete(p.id)}
                  data-testid={`saved-plan-delete-${p.id}`}
                  className="px-2 py-1 text-[10px] font-bold rounded border border-stone-300 text-red-700 hover:bg-red-50"
                  title="Delete plan"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
              {p.is_shared && p.share_token && (
                <div className="mt-1 text-[10px] text-stone-500 truncate font-mono">
                  {shareUrlFor(p.share_token)}
                </div>
              )}
              {pickerForPlanId === p.id && (
                <div className="mt-2 p-2 bg-stone-50 border border-stone-200 rounded-lg" data-testid={`link-picker-${p.id}`}>
                  {p.contact_id && (
                    <div className="flex items-center justify-between gap-2 mb-2 text-[11px]">
                      <span className="text-stone-700">
                        Currently linked to <strong>{p.contact_name || "a contact"}</strong>
                      </span>
                      <button
                        onClick={() => pick(p, null)}
                        data-testid={`link-picker-unlink-${p.id}`}
                        className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded border border-red-300 text-red-700 hover:bg-red-50"
                      >
                        Unlink
                      </button>
                    </div>
                  )}
                  <input
                    value={contactQuery}
                    onChange={(e) => setContactQuery(e.target.value)}
                    placeholder="Search contacts by name or email…"
                    autoFocus
                    data-testid={`link-picker-search-${p.id}`}
                    className="w-full px-2 py-1.5 text-xs bg-white border border-stone-300 rounded"
                  />
                  <div className="mt-1.5 max-h-40 overflow-auto space-y-0.5">
                    {contactsLoading && (
                      <div className="text-[10px] text-stone-500 px-1 py-0.5 flex items-center gap-1">
                        <Loader2 className="w-3 h-3 animate-spin" /> Searching…
                      </div>
                    )}
                    {!contactsLoading && contactQuery.trim().length >= 2 && contactResults.length === 0 && (
                      <div className="text-[10px] text-stone-500 px-1 py-0.5">No matches.</div>
                    )}
                    {contactResults.map((c) => (
                      <button
                        key={c.id}
                        onClick={() => pick(p, c.id)}
                        data-testid={`link-picker-pick-${p.id}-${c.id}`}
                        className="w-full text-left px-2 py-1 text-[11px] rounded hover:bg-white border border-transparent hover:border-stone-300"
                      >
                        <div className="font-bold text-stone-900 truncate">
                          {c.first_name} {c.last_name}
                          {c.organisation && <span className="text-stone-500 font-normal"> · {c.organisation}</span>}
                        </div>
                        <div className="text-[10px] text-stone-500 truncate">{c.email || c.postcode || c.source}</div>
                      </button>
                    ))}
                  </div>
                  <div className="mt-1.5 flex justify-end">
                    <button
                      onClick={closePicker}
                      className="text-[10px] text-stone-500 hover:text-stone-800 px-1"
                    >
                      Close
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {!filtered.length && plans.length > 0 && (
          <div className="text-[11px] text-stone-500 italic px-1 py-2">No plans match "{filter}".</div>
        )}
      </div>
    </div>
  );
}


